import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Receipt, CreditCard, CheckCircle2, Building2 } from 'lucide-react';
import * as api from '../../lib/api';
import { formatCents, formatDate } from '../../lib/format';

const STATUS_STYLES = {
  paid: 'bg-emerald-100 text-emerald-800',
  open: 'bg-sky-100 text-sky-800',
  past_due: 'bg-rose-100 text-rose-800',
  draft: 'bg-slate-200 text-slate-600',
  void: 'bg-slate-200 text-slate-500',
  refunded: 'bg-amber-100 text-amber-900',
};

/**
 * What the tenant owes and how to pay it.
 *
 * Payment goes through Stripe Checkout, so the card is entered on Stripe's own
 * page and no card data touches this app. The button is hidden entirely when
 * card payment is not configured — offering one that can only fail is worse
 * than saying plainly to pay at the office.
 */
export default function BillingPage() {
  const [params, setParams] = useSearchParams();
  const [invoices, setInvoices] = useState(null);
  const [error, setError] = useState(null);
  const [canPay, setCanPay] = useState(false);
  const [busy, setBusy] = useState(null);

  const justPaid = params.get('paid');

  const load = async () => {
    try {
      const payload = await api.listMyInvoices(50);
      setInvoices(payload.invoices);
    } catch (err) {
      setError(err.message);
      setInvoices([]);
    }
  };

  useEffect(() => {
    load();
    api.paymentConfig()
      .then((c) => setCanPay(c.card_payment_enabled))
      .catch(() => setCanPay(false));
  }, []);

  // Stripe redirects back here on success, but the payment is recorded by the
  // webhook — which may land a second or two later. Re-fetch once rather than
  // showing a stale balance the customer has just paid.
  useEffect(() => {
    if (!justPaid) return undefined;
    const timer = setTimeout(load, 1500);
    return () => clearTimeout(timer);
  }, [justPaid]);

  const pay = async (invoice) => {
    setBusy(invoice.public_id); setError(null);
    try {
      const { url } = await api.startCheckout(invoice.public_id);
      window.location.assign(url);
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  };

  const owed = (invoices ?? [])
    .filter((i) => ['open', 'past_due'].includes(i.status))
    .reduce((sum, i) => sum + Number(i.balance_cents), 0);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Billing</h1>
      <p className="mt-1 text-sm text-slate-600">Your invoices and payment history.</p>

      {justPaid && (
        <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
          <div>
            <p className="text-sm font-bold text-emerald-900">Payment received — thank you.</p>
            <p className="mt-0.5 text-xs text-emerald-800">
              It can take a moment to show below. Your receipt is on its way from Stripe.
            </p>
          </div>
          <button type="button" onClick={() => setParams({}, { replace: true })}
                  className="ml-auto text-xs font-bold text-emerald-800 hover:underline">
            Dismiss
          </button>
        </div>
      )}

      {params.get('cancelled') && (
        <p className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          Payment cancelled — nothing was charged.
        </p>
      )}

      {error && <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

      {invoices === null && (
        <div className="grid place-items-center py-16" role="status" aria-label="Loading invoices">
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" />
        </div>
      )}

      {invoices !== null && owed > 0 && (
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Balance due</p>
          <p className="mt-1 text-3xl font-extrabold tabular-nums text-rose-700">{formatCents(owed)}</p>
          {!canPay && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600">
              <Building2 className="h-4 w-4 text-brand-500" aria-hidden="true" />
              Pay at the office — online payment is not set up yet.
            </p>
          )}
        </div>
      )}

      {invoices !== null && invoices.length === 0 && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white py-16 text-center">
          <Receipt className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-bold text-slate-900">Nothing invoiced yet</p>
          <p className="mt-1 text-sm text-slate-500">
            Your first invoice arrives when you move in.
          </p>
        </div>
      )}

      {invoices !== null && invoices.length > 0 && (
        <ul className="mt-5 space-y-3">
          {invoices.map((i) => {
            const balance = Number(i.balance_cents);
            return (
              <li key={i.public_id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
                      {i.invoice_number}
                      <span className={`rounded-full px-2 py-0.5 text-[12px] font-bold ${
                        STATUS_STYLES[i.status] ?? STATUS_STYLES.draft}`}>
                        {i.status.replace('_', ' ')}
                      </span>
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {i.facility_name} · space {i.unit_number} · {formatDate(i.period_start)} to {formatDate(i.period_end)}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">Due {formatDate(i.due_on)}</p>
                  </div>

                  <div className="text-right">
                    <p className="text-lg font-extrabold tabular-nums text-slate-900">
                      {formatCents(i.total_cents)}
                    </p>
                    {balance > 0 ? (
                      <p className="text-xs font-bold text-rose-700">{formatCents(balance)} still owed</p>
                    ) : (
                      <p className="text-xs text-emerald-700">Paid in full</p>
                    )}
                  </div>
                </div>

                {balance > 0 && canPay && (
                  <button type="button" onClick={() => pay(i)} disabled={busy === i.public_id}
                          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-bold text-white hover:bg-brand-700 disabled:opacity-60">
                    {busy === i.public_id
                      ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      : <CreditCard className="h-4 w-4" aria-hidden="true" />}
                    Pay {formatCents(balance)}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canPay && (
        <p className="mt-6 text-xs text-slate-500">
          Payments are handled by Stripe. Your card details are entered on their page and are
          never sent to us or stored here.
        </p>
      )}
    </div>
  );
}
